<template>
  <div class="w-full">
    <div class="w-full text-white text-2xl"  v-if="!error">
      <h1>{{ $route.params.granularityId | displayMonth }}</h1>
    </div>

    <div class="grid grid-cols-2 gap-4 mt-4" v-if="!error">
      <div
        class="col-span-2 md:col-span-1 bg-white rounded-lg overflow-hidden shadow-md"
      >
        <div class="flex flex-row items-center justify-between p-4">
          <h1 class="text-lg">Report Earnings</h1>
        </div>

        <div class="px-4 pb-4 focus-within:font-medium text-4xl">
          {{ stringer_earnings | displayMoney }}
        </div>
      </div>
    </div>

    <div class="w-full flex flex-col bg-white rounded-md shadow-md mt-4 p-4" v-if="!error">
      <div class="text-lg">Statistics</div>
      <div class="grid grid-cols-3 gap-4 mt-4">
        <div
          class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-eye text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.views.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500">Views</span>
          </div>
        </div>

        <div
          class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-clock text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.estimated_minutes_watched.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500">Minutes Watched</span>
          </div>
        </div>

        <div
          class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-stopwatch text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.avg_view_duration.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500"
              >Average View Duration (seconds)</span
            >
          </div>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-4 mt-4 md:mt-6">
        <div
          class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-sync text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.ad_impressions.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500">Ad Impressions</span>
          </div>
        </div>

        <div
          class="col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-thumbs-up text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.likes.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500">Likes</span>
          </div>
        </div>

        <div
          class=" col-span-3 md:col-span-1 flex flex-row items-center content-center space-x-3"
        >
          <div
            class="w-12 h-12 bg-blue-600 rounded-md flex items-center content-center justify-center text-white"
          >
            <i class="fa fa-ad text-lg"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-2xl font-medium">{{
              statistics.monetized_playbacks.toLocaleString()
            }}</span>
            <span class="uppercase text-xs text-gray-500"
              >Monetized Playbacks</span
            >
          </div>
        </div>
      </div>
    </div>

    <div class="w-full flex flex-col bg-white rounded-md shadow-md mt-4 p-4" v-if="!error">
      The views listed below are the views that the video has received during the month. It is not a measure of how many views the video has received since the video was published (unless it was published in the month the report was created). 
    </div>

    <div
      class="-mx-4 mt-8 overflow-hidden shadow-md ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg"
      v-if="!error"
      >
      <table class="min-w-full divide-y divide-gray-300">
        <thead class="bg-gray-50">
          <tr class="hidden lg:table-row">
            <th
              scope="col"
              class="py-3.5 px-3 text-left text-sm font-semibold text-gray-900"
            >
              Video
            </th>
            <th
              scope="col"
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
            >
              Views
            </th>
            <th
              scope="col"
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
            >
              Earnings
            </th>
            <th
              scope="col"
              class="hidden relative py-3.5 pl-3 pr-4 sm:pr-6 lg:table-cell"
            >
              <span class="sr-only">View Report</span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-for="report in reports" v-bind:key="report.id">
            <td scope="col" class="py-3.5 px-3 text-left text-sm text-gray-800">
              {{ report.video.title }}
              <dl class="font-normal lg:hidden">
                <dt class="mt-1 truncate text-gray-700">
                  {{ report.views.toLocaleString() }} views
                </dt>
                <dd class="mt-1 truncate text-gray-700">
                  {{ report.stringer_earnings | displayMoney }}
                </dd>
              </dl>
            </td>
            <td
              scope="col"
              class="hidden px-3 py-3.5 text-left text-sm text-gray-500 lg:table-cell"
            >
              {{ report.views }}
            </td>
            <td
              scope="col"
              class="hidden px-3 py-3.5 text-left text-sm text-gray-500 lg:table-cell"
            >
              {{ report.stringer_earnings | displayMoney }}
            </td>
            <td class="hidden relative py-3.5 px-3 lg:table-cell text-sm">
              <!-- <a href="#" class="text-blue-600 hover:text-blue-900"
                >Statistics</a
              > -->
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="w-full text-white "  v-if="error">
      <h1 class="text-2xl">Access denied.</h1>
      <p>You do not have access to view this content. Ensure the URL is correct.</p>
    </div>
  </div>
</template>

<script>
import * as Config from "@/config";

import moment from "moment";
import VueApexCharts from "vue-apexcharts";
import Card from "@/components/partials/Card";
import CustomTable from "@/components/general/CustomTable";
import Dropdown from "@/components/partials/Dropdown";

export default {
  name: "SocialIncomeMain",
  data() {
    return {
      stringer_earnings: 0,
      statistics: 0,
      reports: [],
      error: false
    };
  },
  mounted() {
    this.getSocialReport();
  },
  methods: {
    async getSocialReport() {
      try {
        let res = await this.$http.get(
          Config.apiPath +
            "/api/v2/stringer/social-reports/" +
            this.$route.params.granularityId +
            "/" +
            this.$route.params.reportId
        );
        this.reports = res.data.reports;
        this.stringer_earnings = res.data.stringer_earnings;
        this.statistics = res.data.statistics;
        this.polling = false;
        this.error = false;
      } catch (e) {
        this.error = true
        console.log(e);
      }
    }
  },
  filters: {
    displayMoney: function(value) {
      var formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      });
      return formatter.format(value);
    },
    displayMonth: function(data) {
      return moment(data, "YYYYMM").format("MMMM");
    }
  }
};
</script>
